import React from 'react';
import { CodeBlock } from '../components/CodeBlock';
import { Activity, BarChart, Server, Terminal } from 'lucide-react';

const Usage: React.FC = () => {
  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="space-y-4">
        <h1 className="text-5xl font-black tracking-tighter text-slate-900 leading-[0.9]">Usage Guide</h1>
        <p className="text-lg text-slate-600 font-medium leading-relaxed">
          Learn how to integrate the Cyxor Collector into your application code to start streaming logs and metrics.
        </p>
      </div>

      <section className="space-y-6 pt-12">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Activity className="text-primary" size={22} />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter">Express.js Integration</h2>
        </div>
        <p className="text-slate-600 leading-relaxed font-medium">
          The easiest way to track your Node.js application is by using our built-in Express middleware. This automatically captures all request metadata, status codes, and durations.
        </p>
        <CodeBlock 
          language="typescript" 
          code={`import { CyxorCollector } from '@cyxor/collector';\nimport express from 'express';\n\nconst app = express();\nconst cyxor = new CyxorCollector({\n  apiKey: 'YOUR_APP_API_KEY',\n});\n\n// Attach the middleware to capture all HTTP telemetry\napp.use(cyxor.expressMiddleware());\n\napp.get('/', (req, res) => {\n  res.send('Telemetery active!');\n});`} 
          filename="app.ts"
        />
      </section>

      <section className="space-y-6 pt-12 border-t border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Terminal className="text-blue-500" size={22} />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter">Manual Logging</h2>
        </div>
        <p className="text-slate-600 leading-relaxed font-medium">
          For granular control, you can send manual log entries with custom levels and metadata strings.
        </p>
        <CodeBlock 
          language="typescript" 
          code={`cyxor.log('Sensitive operation started', 'WARN', {\n  userId: user.id,\n  ip: req.ip,\n  action: 'DELETE_RESOURCES'\n});`} 
          filename="services/auth.ts"
        />
      </section>

      <section className="space-y-6 pt-12 border-t border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
            <BarChart className="text-green-500" size={22} />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter">Custom Metrics</h2>
        </div>
        <p className="text-slate-600 leading-relaxed font-medium">
          Track business-specific performance indicators using the `metric` function.
        </p>
        <CodeBlock 
          language="typescript" 
          code={`cyxor.metric('CHECKOUT_DURATION_MS', 1540, {\n  items: cart.length,\n  coupon: 'DISCOUNT_20'\n});`} 
          filename="controllers/checkout.ts"
        />
      </section>

      <div className="bg-slate-50 border border-slate-200 p-10 rounded-3xl space-y-6 my-16 shadow-sm">
        <h4 className="text-xl font-black text-slate-900 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shadow-sm">
            <Server className="text-primary" size={22} />
          </div>
          Automatic Heartbeats
        </h4>
        <p className="text-lg text-slate-600 leading-relaxed font-medium">
          Once initialized, the Cyxor Collector automatically starts a background <strong className="text-slate-900">Heartbeat Service</strong> that reports system resource metrics (CPU, RAM, and OS Uptime) every 60 seconds without any manual intervention.
        </p>
      </div>
    </div>
  );
};

export default Usage;
